import { CalendarDate } from '@internationalized/date'
import { ArrowLeft } from '@untitledui/icons'
import ms from 'ms'
import { useQueryState } from 'nuqs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import StandaloneLoadingSpinner from '@components/StandaloneLoadingSpinner'
import Button from '@components/designSystem/Button'
import ButtonGroup, { type RadioEntry } from '@components/designSystem/ButtonGroup'
import { Headline } from '@components/designSystem/Headline'
import { DateRangePicker } from '@components/designSystem/date-picker/date-range-picker'
import { Input } from '@components/designSystem/input/input'
import WorkflowTimeline from '@routes/Workflow/Timeline'
import WorkflowInspector from '@routes/Workflow/WorkflowInspector'
import { useRunningWorkflows } from '@state/workflow'

import type { DateValue } from 'react-aria-components'

// Get user's timezone offset in ISO 8601 format (e.g., "+02:00" or "-05:00")
function getTimezoneOffset(): string {
  return new Intl.DateTimeFormat('en', { timeZoneName: 'longOffset' })
    .formatToParts()
    .find(p => p.type === 'timeZoneName')!.value
    .replace('GMT', '')
}

// Format DateValue to readable date string
function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Convert Date to CalendarDate
function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

// Format Date to HH:MM:SS time string (for API/URL params, must be consistent)
function formatTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

// Parse ISO datetime string to extract date and time parts
function parseISODateTime(isoString: string): { date: CalendarDate; time: string } | null {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return null

  return {
    date: dateToCalendarDate(date),
    time: formatTime(date),
  }
}

// Stable parser/serializer functions for useQueryState
const dateRangeParser = (value: string): DateRange => DateRange.parse(value)
const dateRangeSerializer = (range: DateRange): string => range.toString()

class DateRange {
  public start: DateValue | undefined
  public end: DateValue | undefined
  public startTime: string | undefined
  public endTime: string | undefined

  static SEP = '|'
  static tz = getTimezoneOffset()

  static parse(value: string): DateRange {
    const [from, to] = value.split(DateRange.SEP)
    if (!from || !to) return new DateRange()

    const startParsed = parseISODateTime(from)
    const endParsed = parseISODateTime(to)

    if (startParsed && endParsed) {
      return new DateRange(startParsed.date, endParsed.date, startParsed.time, endParsed.time)
    }
    return new DateRange()
  }

  static fromDuration(duration: '1h' | '24h'): DateRange {
    const now = new Date()
    const startDate = new Date(now.getTime() - ms(duration))
    return new DateRange(
      dateToCalendarDate(startDate),
      dateToCalendarDate(now),
      formatTime(startDate),
      formatTime(now)
    )
  }

  constructor(start?: DateValue, end?: DateValue, startTime?: string, endTime?: string) {
    this.start = start
    this.end = end
    this.startTime = startTime
    this.endTime = endTime
  }

  isEmpty(): boolean {
    return !this.start || !this.end
  }

  toQueryParams(): { from: string; to: string } | undefined {
    if (this.isEmpty()) return undefined
    return {
      from: `${this.start!.toString()}T${this.startTime ?? '00:00:00'}${DateRange.tz}`,
      to: `${this.end!.toString()}T${this.endTime ?? '23:59:59'}${DateRange.tz}`,
    }
  }

  toDateRangePickerValue(): { start: DateValue; end: DateValue; startTime?: string; endTime?: string } | null {
    if (this.isEmpty()) return null
    return {
      start: this.start!,
      end: this.end!,
      startTime: this.startTime,
      endTime: this.endTime,
    }
  }

  toString(): string {
    if (this.isEmpty()) return ''
    const params = this.toQueryParams()!
    return `${params.from}${DateRange.SEP}${params.to}`
  }
}

function WorkflowRoute() {
  const navigate = useNavigate()
  const [dateRange, setDateRange] = useQueryState<DateRange>('dateRange', {
    defaultValue: new DateRange(),
    history: 'replace',
    parse: dateRangeParser,
    serialize: dateRangeSerializer,
  })

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null)
  const [selectedWorkflowType, setSelectedWorkflowType] = useState<string>('ALL')
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<'ALL' | 'FAILED'>('ALL')
  const [workflowNameFilter, setWorkflowNameFilter] = useState<string>('')

  const queryParams = dateRange.toQueryParams()
  const { data: workflows = [], isLoading } = useRunningWorkflows(queryParams)

  // Get unique workflow types from the workflows
  const workflowTypes = Array.from(new Set(workflows.map(w => w.workflow))).sort()

  // Filter workflows based on selected type, status, and name
  const filteredWorkflows = workflows.filter(workflow => {
    // Filter by type
    if (selectedWorkflowType !== 'ALL' && workflow.workflow !== selectedWorkflowType) {
      return false
    }

    // Filter by failed status (workflow failed OR any job failed)
    if (selectedStatusFilter === 'FAILED') {
      const workflowFailed = workflow.status === 'FAILED'
      const hasFailedJobs = workflow.jobs.some(job => job.status === 'FAILED')
      if (!workflowFailed && !hasFailedJobs) return false
    }

    // Filter by workflow name (case-insensitive)
    if (workflowNameFilter.trim()) {
      const searchTerm = workflowNameFilter.toLowerCase()
      const nameMatches = workflow.name?.toLowerCase().includes(searchTerm) ?? false
      const idMatches = workflow.id.toLowerCase().includes(searchTerm)
      if (!nameMatches && !idMatches) return false
    }

    return true
  })

  const selectedWorkflow = filteredWorkflows.find(w => w.id === selectedWorkflowId) ?? undefined

  // Handle DateRangePicker change - convert from picker format to DateRange
  function handleDateRangeChange(value: { start: DateValue; end: DateValue; startTime?: string; endTime?: string } | null) {
    if (value) {
      setDateRange(new DateRange(value.start, value.end, value.startTime, value.endTime))
    } else {
      setDateRange(new DateRange())
    }
  }

  if (isLoading) return <StandaloneLoadingSpinner />

  // Create filter entries
  const filterEntries: RadioEntry[] = [
    {
      label: `All (${workflows.length})`,
      value: 'ALL',
    },
    ...workflowTypes.map(type => {
      const count = workflows.filter(w => w.workflow === type).length
      return {
        label: `${type.replace(/_/g, ' ')} (${count})`,
        value: type,
      }
    }),
  ]

  const statusFilterEntries: RadioEntry[] = [
    {
      label: `All (${workflows.length})`,
      value: 'ALL',
    },
    {
      label: `Failed (${workflows.filter(w => w.status === 'FAILED' || w.jobs.some(job => job.status === 'FAILED')).length})`,
      value: 'FAILED',
    },
  ]

  const dateRangeValue = dateRange.toDateRangePickerValue()

  return (
    <div className="space-y-8">
      <div className='m-auto w-full max-w-7xl px-4'>

        <div className='mt-2 flex items-center gap-2'>
          <Button
            variant='outline'
            size='lg'
            className="size-12 rounded-full"
            onClick={() => navigate(-1)}
            aria-label='back'
          >
            <ArrowLeft/>
          </Button>

          <Headline title="Workflows" />
        </div>

        <div className="mt-6 mb-4 space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-medium">Time Range:</h3>
            <div className="flex flex-row items-center gap-3">
              <DateRangePicker
                value={dateRangeValue}
                onChange={handleDateRangeChange}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDateRange(DateRange.fromDuration('1h'))}
              >
                   Last Hour
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setDateRange(DateRange.fromDuration('24h'))}
              >
                   Last 24 Hours
              </Button>
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Filter by Type:</h3>
            <ButtonGroup
              entries={filterEntries}
              onChange={setSelectedWorkflowType}
              selectedValue={selectedWorkflowType}
              className='flex-wrap gap-2'
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Filter by Status:</h3>
            <ButtonGroup
              entries={statusFilterEntries}
              onChange={(value) => setSelectedStatusFilter(value as 'ALL' | 'FAILED')}
              selectedValue={selectedStatusFilter}
              className='flex-wrap gap-2'
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-medium">Filter by Name:</h3>
            <Input
              placeholder="Search workflows..."
              value={workflowNameFilter}
              onChange={setWorkflowNameFilter}
            />
          </div>
        </div>

        {workflows.length === 0 && (
          <p className="mt-6 text-secondary">
            {dateRangeValue
              ? `No workflows found between ${formatDate(dateRangeValue.start.toString())} ${dateRangeValue.startTime ?? '00:00:00'} and ${formatDate(dateRangeValue.end.toString())} ${dateRangeValue.endTime ?? '23:59:59'}.`
              : 'No workflows found in the last 24 hours.'}
          </p>
        )}

        {workflows.length > 0 && (
          <div>
            <WorkflowTimeline workflows={filteredWorkflows} selectWorkflow={setSelectedWorkflowId} />

            {selectedWorkflow && <WorkflowInspector workflow={selectedWorkflow} onClose={() => setSelectedWorkflowId(null)} />}
          </div>
        )}
      </div>
    </div>
  )
}

export default WorkflowRoute